import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Brain, Zap, BookOpen, Target, BarChart3, 
  Settings, ChevronRight, Sparkles, Shield,
  TrendingUp, Users, Clock, CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_ICONS = {
  smart_assignment: Users,
  next_best_action: Zap,
  agent_playbook: BookOpen,
  lead_scoring: Target,
  ai_dashboard: BarChart3
};

const MODULE_COLORS = {
  smart_assignment: 'from-blue-500 to-blue-600',
  next_best_action: 'from-purple-500 to-purple-600',
  agent_playbook: 'from-green-500 to-green-600',
  lead_scoring: 'from-orange-500 to-orange-600',
  ai_dashboard: 'from-pink-500 to-pink-600'
};

const MODULE_ROUTES = {
  smart_assignment: '/admin/ai-intelligence/smart-assignment',
  next_best_action: '/admin/ai-intelligence/next-best-action',
  agent_playbook: '/admin/ai-intelligence/agent-playbook',
  lead_scoring: '/admin/ai-intelligence/lead-scoring',
  ai_dashboard: '/admin/ai-intelligence/dashboard'
};

const MODULE_DESCRIPTIONS = {
  smart_assignment: 'Automatically recommend or assign the best sales agent for each new lead based on historical performance.',
  next_best_action: 'AI-powered suggestions for the optimal next action to take on each lead.',
  agent_playbook: 'Learn strategies from top performers and generate playbooks for the team.',
  lead_scoring: 'Score leads based on conversion probability using AI pattern analysis.',
  ai_dashboard: 'View AI-driven insights, analytics, and recommendations for sales improvement.'
};

const AIIntelligence = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingModule, setTogglingModule] = useState(null);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-intelligence/modules`, {
        params: { token }
      });
      
      if (response.data.success) {
        setModules(response.data.modules);
      }
    } catch (error) {
      console.error('Error fetching AI modules:', error);
      toast.error('Failed to load AI modules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (moduleId, currentEnabled) => {
    setTogglingModule(moduleId);
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/api/ai-intelligence/modules/${moduleId}/toggle?token=${token}`
      );
      
      if (response.data.success) {
        setModules(prev => prev.map(m => 
          m.module_id === moduleId ? { ...m, enabled: response.data.enabled } : m
        ));
        toast.success(`${moduleId.replace('_', ' ')} ${response.data.enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      console.error('Error toggling module:', error);
      toast.error('Failed to toggle module');
    } finally {
      setTogglingModule(null);
    }
  };

  const enabledCount = modules.filter(m => m.enabled).length;

  if (loading) {
    return (
      <div className="p-6" data-testid="ai-intelligence-loading">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="ai-intelligence-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">AI Intelligence</h1>
          </div>
          <p className="text-slate-500">
            Configure and manage AI-powered sales intelligence modules
          </p>
        </div>
        <Badge 
          variant="outline" 
          className={`${enabledCount > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50'}`}
        >
          {enabledCount} of {modules.length} modules active
        </Badge>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-purple-600">AI Modules</p>
                <p className="text-2xl font-bold text-purple-900">{modules.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600">Active</p>
                <p className="text-2xl font-bold text-green-900">{enabledCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Expected Impact</p>
                <p className="text-2xl font-bold text-blue-900">+25%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-600">Time Saved</p>
                <p className="text-2xl font-bold text-orange-900">40%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Module Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {modules.map((module) => {
          const Icon = MODULE_ICONS[module.module_id] || Brain;
          const colorClass = MODULE_COLORS[module.module_id] || 'from-slate-500 to-slate-600';
          const description = MODULE_DESCRIPTIONS[module.module_id] || '';
          const route = MODULE_ROUTES[module.module_id];
          
          return (
            <Card 
              key={module.module_id}
              className={`relative overflow-hidden transition-all duration-200 hover:shadow-lg ${
                module.enabled ? 'ring-2 ring-green-200' : ''
              }`}
              data-testid={`module-card-${module.module_id}`}
            >
              {/* Gradient accent */}
              <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${colorClass}`} />
              
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl bg-gradient-to-br ${colorClass}`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{module.module_name}</CardTitle>
                      <Badge 
                        variant="outline" 
                        className={`mt-1 text-xs ${
                          module.enabled 
                            ? 'bg-green-50 text-green-700 border-green-200' 
                            : 'bg-slate-50 text-slate-500'
                        }`}
                      >
                        {module.enabled ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  
                  <Switch
                    checked={module.enabled}
                    onCheckedChange={() => handleToggleModule(module.module_id, module.enabled)}
                    disabled={togglingModule === module.module_id}
                    data-testid={`toggle-${module.module_id}`}
                  />
                </div>
              </CardHeader>
              
              <CardContent>
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                  {description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Shield className="h-3.5 w-3.5" />
                    <span>Admin Only</span>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(route)}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid={`configure-${module.module_id}`}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Configure
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Section */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <Brain className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">How AI Intelligence Works</h3>
              <p className="text-sm text-slate-600 mb-3">
                Each module can be enabled or disabled independently. When enabled, the AI system
                will analyze your sales data and provide intelligent recommendations to improve
                conversion rates and agent productivity.
              </p>
              <ul className="text-sm text-slate-500 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Toggle modules on/off without affecting existing workflows
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Configure each module's behavior through settings
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  AI learns from your team's historical performance data
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIIntelligence;
